import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Layout,
  Menu,
  Modal,
  Space,
  theme,
  Avatar,
  Typography,
} from "antd";
import SideMenu from "./SideMenu";
import ContentBody from "./ContentBody";
import useSessionTimeout from "./useSessionTimeout";
import SessionTimeoutAlertModal from "./SessionTimeoutAlertModal";
import logoWeblamps from "../Pages/Image/logo-lamps23.png"; // Assurez-vous que le chemin est correct
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  HomeOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/fr";
// dayjs.locale("fr");

import { api_DisplayName } from "../Services/Constantes";
import { menuConfig, findMenuItemByPath } from "../Services/MenuConfig";

const { Header, Content, Footer, Sider } = Layout;

const SESSION_WARNING_DELAY = 25 * 60 * 1000; // 25 minutes
const SESSION_GRACE_PERIOD = 5 * 60 * 1000; // 5 minutes
// const SESSION_WARNING_DELAY = 10 * 1000; // 1 minute pour les tests
// const SESSION_GRACE_PERIOD = 3 * 60 * 1000; // 3 minutes pour les tests
/**
 * Génère les items du breadcrumb synchronisé avec la configuration centralisée du menu
 */
const getBreadcrumbItems = (pathname, rolename) => {
  const breadcrumbItems = [];

  // Ajouter l'accueil uniquement si pas Point Focal
  if (rolename !== 'Point Focal') {
    breadcrumbItems.push({
      title: (
        <Link
          to="/dashboard"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          onClick={() => {
            window.dispatchEvent(new CustomEvent('selectMenuItem', { detail: { key: 'dashboard' } }));
          }}
        >
          <HomeOutlined />
          <span>Accueil</span>
        </Link>
      ),
    });
  }

  // Gérer les cas spéciaux (edit, view, show)
  const pathParts = pathname.split('/').filter(p => p);

  // Cas : /participants/edit/:numcont ou /participants/view/:numcont ou /fournisseurs/edit/:numFourn
  if (pathParts.length >= 2 && (pathParts[1] === 'edit' || pathParts[1] === 'view')) {
    const basePath = `/${pathParts[0]}`;
    const { parent, child } = findMenuItemByPath(basePath, menuConfig);

    if (parent && child) {
      breadcrumbItems.push({
        title: <span style={{ color: '#8c8c8c' }}>{parent.label}</span>,
      });
      breadcrumbItems.push({
        title: <Link to={basePath} style={{ color: '#8c8c8c' }}>{child.label}</Link>,
      });
      breadcrumbItems.push({
        title: <span style={{ color: '#1890ff', fontWeight: 500 }}>
          {pathParts[1] === 'edit' ? 'Modifier' : 'Détails'}
        </span>,
      });
      return breadcrumbItems;
    }
  }

  // Cas : /factures/show/:numenreg
  if (pathParts.length >= 2 && pathParts[1] === 'show') {
    const basePath = `/${pathParts[0]}`;
    const { parent, child } = findMenuItemByPath(basePath, menuConfig);

    if (parent && child) {
      breadcrumbItems.push({
        title: <span style={{ color: '#8c8c8c' }}>{parent.label}</span>,
      });
      breadcrumbItems.push({
        title: <Link to={basePath} style={{ color: '#8c8c8c' }}>{child.label}</Link>,
      });
      breadcrumbItems.push({
        title: <span style={{ color: '#1890ff', fontWeight: 500 }}>Traitement</span>,
      });
      return breadcrumbItems;
    }
  }

  // Chercher dans la configuration centralisée (cas normal)
  const { parent, child } = findMenuItemByPath(pathname, menuConfig);

  if (child) {
    if (parent) {
      // C'est un sous-item
      breadcrumbItems.push({
        title: <span style={{ color: '#8c8c8c' }}>{parent.label}</span>,
      });
      breadcrumbItems.push({
        title: <span style={{ color: '#1890ff', fontWeight: 500 }}>{child.label}</span>,
      });
    } else {
      // C'est un item racine
      breadcrumbItems.push({
        title: <span style={{ color: '#1890ff', fontWeight: 500 }}>{child.label}</span>,
      });
    }
  }

  return breadcrumbItems;
};


const LayoutComp = ({ onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [username, setUsername] = useState("");
  const [rolename, setRolename] = useState("");
  const location = useLocation();

  const navigate = useNavigate();
  const {
    isSessionAlertVisible,
    remainingGraceSeconds,
    gracePeriodProgress,
    warningDelayMinutes,
    gracePeriodMinutes,
    totalDurationMinutes,
    pauseSessionTimeout,
    resumeSessionTimeout,
    handleContinueSession,
    handleImmediateLogout,
  } = useSessionTimeout({
    onLogout,
    warningDelay: SESSION_WARNING_DELAY,
    gracePeriod: SESSION_GRACE_PERIOD,
  });

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handlepagemdp = () => {
    navigate(`/profil`);
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  useEffect(() => {
    const storedRolename = localStorage.getItem("role_name");
    if (storedRolename) {
      setRolename(storedRolename);
    }
  }, []);

  // Rediriger automatiquement les utilisateurs "Point Focal" vers le 1er sous-menu
  useEffect(() => {
    if (rolename === 'Point Focal') {
      const currentPath = location.pathname;
      if (currentPath === '/' || currentPath === '/dashboard' || currentPath === '/docs') {
        navigate('/bons_pharmacies', { replace: true });
      }
    }
  }, [rolename, location.pathname, navigate]);


  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  const logout = () => {
    pauseSessionTimeout();
    setLogoutModalVisible(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.clear();
    onLogout();
  };

  const handleLogoutCancel = () => {
    setLogoutModalVisible(false);
    resumeSessionTimeout();
  };

  const userInitials = (() => {
    const parts = (username || "").trim().split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return "U";
  })();

  const gestionMenu = (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow:
          "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
        padding: 0,
        minWidth: 240,
        overflow: "hidden",
      }}
    >
      {/* Header moderne avec avatar et infos utilisateur */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "linear-gradient(135deg, #fafafa 0%, #ffffff 100%)",
        }}
      >
        <Avatar
          size={42}
          style={{
            background: "linear-gradient(135deg, #1890ff 0%, #0097e6 100%)",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          {userInitials}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: "#1f1f1f",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {username || "Utilisateur"}
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: 4,
              fontSize: 11,
              color: "#595959",
              background: "#f5f5f5",
              borderRadius: 12,
              padding: "2px 8px",
            }}
          >
            {rolename || "Rôle non défini"}
          </div>
        </div>
      </div>

      {/* Options via Menu Ant Design */}
      <Menu
        selectable={false}
        mode="inline"
        onClick={({ key }) => {
          if (key === "profile") handlepagemdp();
          if (key === "logout") logout();
        }}
        items={[
          {
            key: "profile",
            icon: <UserOutlined />,
            label: <span style={{ fontSize: 14 }}>Mon Profil</span>,
          },
          { type: "divider" },
          {
            key: "logout",
            icon: <LogoutOutlined />,
            danger: true,
            label: <span style={{ fontSize: 14 }}>Déconnexion</span>,
          },
        ]}
        style={{ border: "none", padding: 8 }}
      />
    </div>
  );

  dayjs.locale("fr");
  // Générer la date du jour au format demandé
  const dateDuJour = `Nous sommes le ${dayjs().format("dddd D MMMM YYYY")}`;


  return (
    <Layout style={{ minHeight: "100vh" }}>
      <SessionTimeoutAlertModal
        open={isSessionAlertVisible}
        warningDelayMinutes={warningDelayMinutes}
        gracePeriodMinutes={gracePeriodMinutes}
        totalDurationMinutes={totalDurationMinutes}
        remainingGraceSeconds={remainingGraceSeconds}
        gracePeriodProgress={gracePeriodProgress}
        onContinue={handleContinueSession}
        onLogout={handleImmediateLogout}
      />

      <Modal
        open={logoutModalVisible}
        onCancel={handleLogoutCancel}
        footer={null}
        centered
        width={400}
        closable={false}
        styles={{ body: { padding: '32px 24px', textAlign: 'center' } }}
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Avatar
            size={64}
            icon={<LogoutOutlined />}
            style={{ backgroundColor: '#fff1f0', color: '#ff4d4f', fontSize: 28 }}
          />
          <div>
            <Typography.Title level={4} style={{ marginBottom: 4 }}>
              Déconnexion
            </Typography.Title>
            <Typography.Text type="secondary">
              Êtes-vous sûr de vouloir vous déconnecter,{' '}
              <strong>{username || 'Utilisateur'}</strong> ?
            </Typography.Text>
          </div>
          <Space style={{ width: '100%', justifyContent: 'center' }} size={12}>
            <Button
              size="large"
              onClick={handleLogoutCancel}
              style={{ minWidth: 120, borderRadius: 8 }}
            >
              Annuler
            </Button>
            <Button
              size="large"
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogoutConfirm}
              style={{ minWidth: 120, borderRadius: 8 }}
            >
              Se déconnecter
            </Button>
          </Space>
        </Space>
      </Modal>

      {/* Header est en haut, à 100% de la largeur */}
      <Header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#053f14",
          padding: 0,
          height: "50px",
          background: colorBgContainer,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            paddingLeft: 8,
            width: 260,
          }}
        >
          {/* Ajout de l'image dans le Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              background: "#ffffff",
              borderRadius: 12,
              border: "1px solid rgba(0, 21, 41, 0.12)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              padding: "4px 16px",
              minHeight: 34,
            }}
          >
            <img
              src={logoWeblamps}
              alt="Logo"
              style={{ width: "120%", height: "auto", objectFit: "contain" }}
            />
          </div>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: 16,
              width: 38,
              height: 38,
              marginLeft: "auto",
              borderRadius: 10,
              background: "linear-gradient(135deg, rgba(0, 21, 41, 0.08) 0%, rgba(22, 119, 255, 0.14) 100%)",
              border: "1px solid rgba(0, 21, 41, 0.14)",
              color: "#001529",
              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(0, 21, 41, 0.14) 0%, rgba(22, 119, 255, 0.22) 100%)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, rgba(0, 21, 41, 0.08) 0%, rgba(22, 119, 255, 0.14) 100%)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          />
        </div>

        {/* Date du jour au centre */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 16px",
              borderRadius: 999,
              background: "rgba(0, 21, 41, 0.06)",
              border: "1px solid rgba(0, 21, 41, 0.1)",
              color: "#001529",
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: 0.2,
              userSelect: "none",
            }}
          >
            <CalendarOutlined style={{ fontSize: 14, color: "#1677ff" }} />
            {dateDuJour.charAt(0).toUpperCase() + dateDuJour.slice(1)}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingRight: "12px",
            paddingLeft: "8px",
          }}
        >
          {/*   <div style={{ color: "black", fontSize: "15px" }}>
            <strong>
              {
                <Dropdown overlay={gestionMenu} trigger={["click"]}>
                  <UserOutlined />
                </Dropdown>
              }
              &nbsp;{username}({rolename})
            </strong>
          </div> */}
          <Dropdown
            overlay={gestionMenu}
            trigger={["click"]}
            placement="bottomRight"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 12px",
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 0.22s ease",
                background: "linear-gradient(135deg, rgba(0, 21, 41, 0.04) 0%, rgba(22, 119, 255, 0.08) 100%)",
                border: "1px solid rgba(0, 21, 41, 0.1)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                backdropFilter: "blur(2px)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(0, 21, 41, 0.08) 0%, rgba(22, 119, 255, 0.14) 100%)";
                e.currentTarget.style.boxShadow = "0 8px 18px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(0, 21, 41, 0.04) 0%, rgba(22, 119, 255, 0.08) 100%)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <Avatar
                size={32}
                style={{
                  background: "linear-gradient(135deg, #001529 0%, #003a8c 100%)",
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow: "0 4px 10px rgba(0, 21, 41, 0.25)",
                }}
              >
                {userInitials}
              </Avatar>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#001529" }}>
                  {username || "Utilisateur"}
                </div>
                <div style={{ fontSize: 11, color: "#595959", letterSpacing: 0.2 }}>
                  {rolename || ""}
                </div>
              </div>
            </div>
          </Dropdown>

        </div>
      </Header>

      {/* Sider, Content et Footer sont contenus dans le Layout principal */}
      <Layout>
        <Sider trigger={null} collapsible collapsed={collapsed} width={260}  /* style={{ background: "#053f14" }} */ >
          <div
            style={{
              color: "white",
              textAlign: "center",
              marginTop: 12,
              fontSize: "16px",

            }}>
            <strong style={{ color: "whitesmoke", }}>{api_DisplayName}</strong>
          </div>
          <SideMenu onLogout={onLogout} />
        </Sider>

        <Layout>
          <div
            style={{
              margin: "5px 0",
              padding: "8px 16px",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02)",
            }}
          >
            <Breadcrumb
              items={getBreadcrumbItems(location.pathname, rolename)}
              separator="/"
              style={{
                fontSize: 14,
              }}
            />
          </div>
          <Content
            style={{
              margin: "12px 8px 0",
            }}
          >
            <Card
            /*  style={{
               padding: 0,
               minHeight: 360,
               background: colorBgContainer,
             }} */
            >
              <ContentBody />
            </Card>
          </Content>
          <Footer
            style={{
              textAlign: "center",
            }}
          >
            WebLamps © V2 - 2025 Created by A2SMI
          </Footer>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default LayoutComp;
